import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TrendingUp, Users, ChevronRight, Car, Bike, Rss, Plus, Check } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { trackGarageFollowed } from '@/lib/analytics';
import { EvSalesLeaderboard } from '@/components/widgets/EvSalesLeaderboard';
import { DailyGoalWidget } from '@/components/widgets/DailyGoalWidget';
import { ContinueLearningWidget } from '@/components/widgets/ContinueLearningWidget';
import { MicroQuiz } from '@/components/widgets/MicroQuiz';
import { SessionProgressBar } from '@/components/widgets/SessionProgressBar';
import { WeeklyGoalWidget } from '@/components/widgets/WeeklyGoalWidget';
import { GarageSpotlight } from '@/components/widgets/GarageSpotlight';
import { GarageActivityPulse } from '@/components/widgets/GarageActivityPulse';
import { TrendingDiscussions } from '@/components/widgets/TrendingDiscussions';
import { F1StandingsWidget } from '@/components/widgets/F1StandingsWidget';
import { MotoGPStandingsWidget } from '@/components/widgets/MotoGPStandingsWidget';
import { DelhiICEBanCountdown } from '@/components/widgets/DelhiICEBanCountdown';
import { ThisWeekLaunches } from '@/components/widgets/ThisWeekLaunches';
import { LaunchCalendarWidget } from '@/components/widgets/LaunchCalendarWidget';
import { CommunityBuzz } from '@/components/feed/CommunityBuzz';
import { VehicleClusterWidget } from '@/components/widgets/VehicleClusterWidget';
import { WhoToFollow } from '@/components/widgets/WhoToFollow';

// Garage logo/image mappings based on garage name patterns
const garageImages: Record<string, string> = {
  'bmw': 'https://images.unsplash.com/photo-1617531653332-bd46c24f2068?w=100&h=100&fit=crop',
  'porsche': 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=100&h=100&fit=crop',
  'ev': 'https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=100&h=100&fit=crop',
  'tesla': 'https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=100&h=100&fit=crop',
  'electric': 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=100&h=100&fit=crop',
  'track': 'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=100&h=100&fit=crop',
  'racing': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&h=100&fit=crop',
  'adventure': 'https://images.unsplash.com/photo-1558981806-ec527fa84c39?w=100&h=100&fit=crop',
  'ducati': 'https://images.unsplash.com/photo-1558981403-c5f9899a28bc?w=100&h=100&fit=crop',
  'harley': 'https://images.unsplash.com/photo-1558981359-219d6364c9c8?w=100&h=100&fit=crop',
  'motorcycle': 'https://images.unsplash.com/photo-1558981285-6f0c94958bb6?w=100&h=100&fit=crop',
  'ferrari': 'https://images.unsplash.com/photo-1592198084033-aade902d1aae?w=100&h=100&fit=crop',
  'lamborghini': 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=100&h=100&fit=crop',
  'mercedes': 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=100&h=100&fit=crop',
  'audi': 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=100&h=100&fit=crop',
  'jdm': 'https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=100&h=100&fit=crop',
  'classic': 'https://images.unsplash.com/photo-1489824904134-891ab64532f1?w=100&h=100&fit=crop',
  'vintage': 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=100&h=100&fit=crop',
  'off-road': 'https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=100&h=100&fit=crop',
  'offroad': 'https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=100&h=100&fit=crop',
  'muscle': 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=100&h=100&fit=crop',
};

// Get image URL for garage based on name matching
function getGarageImage(garageName: string): string | null {
  const nameLower = garageName.toLowerCase();
  for (const [key, url] of Object.entries(garageImages)) {
    if (nameLower.includes(key)) {
      return url;
    }
  }
  return null;
}

// Determine if garage is motorcycle-related
function isMotorcycleGarage(garageName: string): boolean {
  const nameLower = garageName.toLowerCase();
  const motorcycleKeywords = ['motorcycle', 'bike', 'rider', 'ducati', 'harley', 'yamaha', 'honda', 'kawasaki', 'ktm', 'triumph', 'adventure', 'moto'];
  return motorcycleKeywords.some(keyword => nameLower.includes(keyword));
}

export function RightSidebar() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // T-127: one auth source. `!!accessToken` was a third derivation of the same fact.
  const isAuthed = useAuthStore((s) => s.isAuthenticated);

  // T-178/T-180: the three sidebar trending rails are DELETED. Trending Today
  // (TrendingStrip, main column) is the single trending surface now.
  //
  // The spec said two rails; there were THREE, and two of them were both
  // titled "Trending This Week" while resolving to DIFFERENT queries — one
  // trending POSTS (limit 5, days 7), one trending TAGS (limit 5, hours 168).
  // Same words, different data, stacked in one column. That is the T-179 shape
  // again: identical label is not evidence of a duplicate, so check the query.
  //
  // Their queries, refs and the ANALYTICS-100 impression observer go with them —
  // a deleted rail cannot be impressed, and leaving the observer would report
  // impressions for components that no longer render.

  const { data: trendingGarages, isLoading: garagesLoading } = useQuery({
    queryKey: ['garages', 'trending'],
    queryFn: () => api.garages.trending({ limit: 5, days: 7 }),
    staleTime: 60 * 60 * 1000, // 1 hour (matches backend cache)
  });

  const { data: myGarages } = useQuery({
    queryKey: ['garages', 'my'],
    queryFn: () => api.garages.my(),
    enabled: isAuthed,
    staleTime: 5 * 60 * 1000,
  });
  const joinedSlugs = new Set(myGarages?.items.map((g) => g.slug) ?? []);

  const joinGarageMutation = useMutation({
    mutationFn: (slug: string) => api.garages.join(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['garages', 'my'] });
      queryClient.invalidateQueries({ queryKey: ['garages', 'trending'] });
    },
  });

  const handleJoinGarage = (slug: string) => {
    if (!isAuthed) {
      navigate('/login');
      return;
    }
    trackGarageFollowed(slug, 'feed');
    joinGarageMutation.mutate(slug);
  };

  const { data: followingSources, isLoading: sourcesLoading } = useQuery({
    queryKey: ['sources', 'following'],
    queryFn: () => api.sources.following(),
    staleTime: 5 * 60 * 1000,
    enabled: isAuthed,
  });

  return (
    <aside className="hidden w-80 flex-shrink-0 p-4 xl:block">



      {/* Your Sources */}
      <div className="mt-4 rounded-2xl border border-border-primary bg-bg-secondary overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border-primary bg-gradient-to-r from-primary-500/5 to-transparent">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500/10">
            <Rss className="h-4 w-4 text-text-tertiary" />
          </div>
          <h3 className="font-semibold text-text-primary">Your Sources</h3>
          {followingSources?.items && followingSources.items.length > 0 && (
            <span className="ml-auto text-xs text-text-tertiary">
              {followingSources.items.length}
            </span>
          )}
        </div>

        <div className="p-2">
          {sourcesLoading ? (
            <div className="space-y-2 p-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-2 animate-pulse">
                  <div className="h-8 w-8 rounded-lg bg-bg-tertiary" />
                  <div className="h-4 w-24 rounded bg-bg-tertiary" />
                </div>
              ))}
            </div>
          ) : followingSources?.items && followingSources.items.length > 0 ? (
            followingSources.items.slice(0, 3).map((source) => (
              <Link
                key={source.id}
                to={`/source/${source.id}`}
                className="flex items-center gap-2 rounded-lg px-3 py-2 transition-colors hover:bg-bg-tertiary group"
              >
                {source.logoUrl ? (
                  <img
                    src={source.logoUrl}
                    alt=""
                    className="h-7 w-7 rounded-md"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="flex h-7 w-7 items-center justify-center rounded-md bg-bg-tertiary text-xs font-bold text-text-tertiary">
                    {source.name.charAt(0)}
                  </div>
                )}
                <span className="truncate text-sm font-medium text-text-secondary group-hover:text-primary-400 transition-colors">
                  {source.name}
                </span>
              </Link>
            ))
          ) : (
            <div className="px-3 py-4 text-center">
              <p className="text-sm text-text-tertiary mb-2">
                Follow sources to customize your feed
              </p>
              <Link
                to="/sources"
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-600 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add Sources
              </Link>
            </div>
          )}
        </div>

        {followingSources?.items && followingSources.items.length > 0 && (
          <Link
            to="/sources"
            className="flex items-center justify-center gap-1 border-t border-border-primary py-3 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
          >
            Manage sources
            <ChevronRight className="h-4 w-4" />
          </Link>
        )}
      </div>

      {/* This Week in Launches — UI-030 */}
      <div className="mt-4">
        <ThisWeekLaunches />
      </div>

      {/* Launch Calendar — UI-036 */}
      <div className="mt-4">
        <LaunchCalendarWidget />
      </div>

      {/* Garage Spotlight — recent public garage activity */}
      <div className="mt-4">
        <GarageSpotlight />
      </div>

      {/* UI-072: Garage Activity Pulse — recent joins & posts */}
      <div className="mt-4">
        <GarageActivityPulse />
      </div>

      {/* F1 2026 Standings — UI-024 */}
      <div className="mt-4">
        <F1StandingsWidget />
      </div>

      {/* MotoGP 2026 Standings — UI-033 */}
      <div className="mt-4">
        <MotoGPStandingsWidget />
      </div>

      {/* Delhi ICE 2W Ban Countdown — UI-025 */}
      <div className="mt-4">
        <DelhiICEBanCountdown />
      </div>

      {/* UI-041: Community Buzz — Reddit r/IndianBikes + r/CarsIndia */}
      <div className="mt-4">
        <CommunityBuzz />
      </div>

      {/* Trending Discussions — COMMUNITY-011 */}
      <div className="mt-4">
        <TrendingDiscussions />
      </div>

      {/* Top Garages This Week */}
      <div className="mt-4 rounded-2xl border border-border-primary bg-bg-secondary overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border-primary bg-gradient-to-r from-emerald-500/5 to-transparent">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10">
            <TrendingUp className="h-4 w-4 text-emerald-500" />
          </div>
          <h3 className="font-semibold text-text-primary">Top Garages This Week</h3>
        </div>

        <div className="p-2">
          {garagesLoading ? (
            <div className="space-y-3 p-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="h-6 w-6 rounded bg-bg-tertiary flex-shrink-0" />
                  <div className="h-10 w-10 rounded-lg bg-bg-tertiary flex-shrink-0" />
                  <div className="flex-1">
                    <div className="h-4 w-24 rounded bg-bg-tertiary" />
                    <div className="mt-1 h-3 w-16 rounded bg-bg-tertiary" />
                  </div>
                </div>
              ))}
            </div>
          ) : trendingGarages?.items && trendingGarages.items.length > 0 ? (
            trendingGarages.items.map((garage, index) => {
              const garageImage = getGarageImage(garage.name);
              const isMoto = isMotorcycleGarage(garage.name);
              const joined = joinedSlugs.has(garage.slug);
              const pendingJoin = joinGarageMutation.isPending && joinGarageMutation.variables === garage.slug;

              return (
                <div
                  key={garage.id}
                  className="flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-bg-tertiary group"
                >
                  <Link
                    to={`/garage/${garage.slug}`}
                    className="flex min-w-0 flex-1 items-center gap-3"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded text-xs font-bold text-text-tertiary bg-bg-tertiary group-hover:bg-bg-secondary flex-shrink-0">
                      {index + 1}
                    </span>
                    <div className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg">
                      {garageImage ? (
                        <img
                          src={garageImage}
                          alt=""
                          className="h-full w-full object-cover transition-transform group-hover:scale-110"
                        />
                      ) : garage.emoji ? (
                        <div className="flex h-full w-full items-center justify-center bg-bg-tertiary text-lg">
                          {garage.emoji}
                        </div>
                      ) : (
                        <div className={`flex h-full w-full items-center justify-center ${
                          isMoto
                            ? 'bg-cat-motorcycles/15 text-cat-motorcycles'
                            : 'bg-cat-cars/15 text-cat-cars'
                        }`}>
                          {isMoto ? <Bike className="h-5 w-5" /> : <Car className="h-5 w-5" />}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-text-primary group-hover:text-primary-400 transition-colors">
                        {garage.name}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-text-tertiary">
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {garage.memberCount.toLocaleString()}
                        </span>
                        {garage.newPosts > 0 && (
                          <span className="text-emerald-500">+{garage.newPosts} posts</span>
                        )}
                        {garage.newMembers > 0 && (
                          <span className="text-text-tertiary">+{garage.newMembers} new</span>
                        )}
                      </div>
                    </div>
                  </Link>
                  {joined ? (
                    <span className="flex-shrink-0 inline-flex items-center gap-1 rounded-lg bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-400 ring-1 ring-emerald-500/25">
                      <Check className="h-3 w-3" />
                      Joined
                    </span>
                  ) : (
                    <button
                      onClick={() => handleJoinGarage(garage.slug)}
                      disabled={pendingJoin}
                      className="flex-shrink-0 rounded-lg bg-primary-500 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
                    >
                      {pendingJoin ? '…' : 'Join'}
                    </button>
                  )}
                </div>
              );
            })
          ) : (
            <p className="px-3 py-4 text-center text-sm text-text-tertiary">
              No garage activity this week
            </p>
          )}
        </div>

        <Link
          to="/explore?tab=garages"
          className="flex items-center justify-center gap-1 border-t border-border-primary py-3 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
        >
          Discover garages
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>

      {/* COMMUNITY-003: Who to Follow */}
      <div className="mt-4">
        <WhoToFollow />
      </div>

      {/* AI-003: Vehicle Entity Clusters */}
      <div className="mt-4">
        <VehicleClusterWidget />
      </div>

      {/* Continue Learning CTA */}
      <div className="mt-4">
        <ContinueLearningWidget />
      </div>

      {/* Micro Quiz */}
      <div className="mt-4">
        <MicroQuiz />
      </div>

      {/* Session Progress */}
      <div className="mt-4">
        <SessionProgressBar />
      </div>

      {/* Daily Reading Goal */}
      <div className="mt-4">
        <DailyGoalWidget />
      </div>

      {/* Weekly Reading Goal — UI-037 */}
      <div className="mt-4">
        <WeeklyGoalWidget />
      </div>

      {/* EV Sales Leaderboard */}
      <div className="mt-4">
        <EvSalesLeaderboard />
      </div>

      {/* Footer Links */}
      <div className="mt-4 px-2">
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-text-tertiary">
          <a href="#" className="hover:text-text-secondary">About</a>
          <a href="#" className="hover:text-text-secondary">Terms</a>
          <a href="#" className="hover:text-text-secondary">Privacy</a>
          <a href="#" className="hover:text-text-secondary">Help</a>
        </div>
        <p className="mt-2 text-xs text-text-tertiary">
          &copy; {new Date().getFullYear()} MotoWalla
        </p>
      </div>
    </aside>
  );
}
